import {
  formateDateToPreferredLanguage,
  formatMessage,
  formatSehorseLastUpdateDate,
} from "../../utils/helpers/LanguageHelper";
import { sendDocumentToInDesign } from "../../api/document/InDesign";
import { TreeViewDataItem } from "../cpm/DocumentPartTree";
import { LinkedDocumentInfo } from "../../api/models/linked-document-info";
import {
  hasNoDefaultServiceProvider,
  hasNoIxbrlSetting,
  sendDocumentToIXBRLAndRefresh,
} from "../../pages/seahorse/SeahorseUtils";
import { ServiceProvider } from "../../api/models/service-provider";
import { IxbrlSetting } from "../../api/models/ixbrl-setting";
import { FlatPathTreeItem } from "../generic/FlatPathTreeExplorer";
import { DocumentPartWithStatus } from "../../api/models/document-part-with-status";
import { getWorkflowStatus } from "../cpm/DocumentPartTreeUtils";
import { exportWorkflowHistoryAsync } from "../../redux/feature/document/document-slice";

const SUBMITTED: string = "50";
const APPROVED1: string = "30";
const APPROVED2: string = "31";
const APPROVED3: string = "32";
const APPROVED4: string = "33";
const APPROVED5: string = "34";

export function addRenameDocumentItemMenu(
  items: any[],
  itemSelected: TreeViewDataItem,
  onRenameDocument: (item: FlatPathTreeItem) => void,
  idPartRoot: string,
  notAuthorizedUserForRename: boolean,
  documentPartWithStatus: DocumentPartWithStatus[]
) {
  if (onRenameDocument) {
    items.push({
      key: "rename",
      text: formatMessage("cof.base.rename"),
      onClick: () => {
        onRenameDocument(itemSelected);
      },
      // NOTE: allow rename also for document parts at higher depth (?) - it actually does not work since the expanded property is reset to false when the rename button is clicked
      hidden: itemSelected.key == idPartRoot,
      disabled:
        notAuthorizedUserForRename ||
        (documentPartWithStatus?.length && haveSubmittedOrApprovedStatus(itemSelected, documentPartWithStatus)),
    });
  }
}

export function addAlignDocumentItemMenu(
  items: any[],
  itemSelected: TreeViewDataItem,
  onAlignDocument: (item: FlatPathTreeItem) => void,
  notAuthorizedUserForEditRoot: boolean
) {
  if (onAlignDocument) {
    items.push({
      key: "align",
      text: formatMessage("cof.base.runalignment"),
      onClick: () => {
        onAlignDocument(itemSelected);
      },
      hidden: !(
        itemSelected.data.documentPart?.hasChildren ||
        (itemSelected as any).documentPart?.hasChildren ||
        itemSelected.data.hasChildren
      ),
      disabled: notAuthorizedUserForEditRoot,
    });
  }
}

export function addExportDifferencesItemMenu(
  items: any[],
  itemSelected: TreeViewDataItem,
  onCheckDifferencesDocument: (item: FlatPathTreeItem) => void
) {
  if (onCheckDifferencesDocument) {
    items.push({
      key: "export_differences",
      text: formatMessage("cof.base.exportdifferences"),
      onClick: () => {
        onCheckDifferencesDocument(itemSelected);
      },
    });
  }
}

export function addExportWorkflowItemMenu(
  items: any[],
  itemSelected: TreeViewDataItem,
  isCertifiedOrApprovedOrRejected: boolean
) {
  if (itemSelected.data.root && isCertifiedOrApprovedOrRejected) {
    items.push({
      key: "export_submission_history",
      text: formatMessage("cof.base.export.workflow"),
      onClick: async () => {
        const payload = {
          documentId: itemSelected.data.documentId ? itemSelected.data.documentId : itemSelected.data.id,
          text: "export-submission-history-" + Date.now() + ".xlsx",
          hasChildren: false,
          expanded: true,
          checked: true,
          items: [],
          disabled: false,
        };
        await exportWorkflowHistoryAsync(payload);
        return;
      },
    });
  }
}

export function addDeleteItemMenu(
  items: any[],
  idPartRoot: string,
  itemSelected: TreeViewDataItem,
  onDeleteDocument: (item: FlatPathTreeItem) => void,
  notAuthorizedUserForDelete: boolean,
  documentPartWithStatus: DocumentPartWithStatus[],
  itemHierarchicalIndex: string
) {
  if (onDeleteDocument) {
    items.push({
      key: "delete",
      text: formatMessage("cof.base.delete"),
      onClick: () => {
        onDeleteDocument(itemSelected);
      },
      hidden: !(itemSelected.key != idPartRoot && (itemHierarchicalIndex.match(/_/g)?.length ?? 0) < 1),
      disabled:
        notAuthorizedUserForDelete ||
        (documentPartWithStatus?.length && haveSubmittedOrApprovedStatus(itemSelected, documentPartWithStatus)),
    });
  }
}

export function addExportItemMenu(
  items: any[],
  itemSelected: TreeViewDataItem,
  onExportDocument: (item: FlatPathTreeItem) => void
) {
  if (onExportDocument) {
    items.push({
      key: "export",
      text: formatMessage("cof.base.export"),
      onClick: () => {
        onExportDocument(itemSelected);
      },
    });
  }
}

export function addSendCopyToIXBRLItemMenu(
  items: any[],
  rootId: string,
  itemSelected: TreeViewDataItem,
  defaultServiceProvider: ServiceProvider,
  notAuthorizedUserForSendCopyIXBRL: boolean,
  allIxbrlSettings: IxbrlSetting[],
  seahorseDocumentLinked: LinkedDocumentInfo[],
  isSendingIxbrl: boolean
) {
  if (!notAuthorizedUserForSendCopyIXBRL) {
    const selected = itemSelected;
    items.push({
      id: "sendcopyixbrl_menuitem",
      key: "sendcopyixbrl",
      text: formatMessage("cof.base.sendcopyixbrl"),
      onClick: () => {
        sendDocumentToIXBRLAndRefresh(selected.data, defaultServiceProvider.id);
      },
      hidden: selected.key != rootId || hasNoIxbrlSetting(rootId, allIxbrlSettings),
      disabled: hasNoDefaultServiceProvider(defaultServiceProvider) || isSendingIxbrl,
    });
    items.push({
      key: "updated",
      text: formatSehorseLastUpdateDate(seahorseDocumentLinked, selected.data.driveItemId),
      hidden: !formatSehorseLastUpdateDate(seahorseDocumentLinked, selected.data.driveItemId).length,
      disabled: "true",
      className: "sentDateUpdate",
    });
  }
}

export function addSendToInDesignItemMenu(
  items: any[],
  rootId: string,
  itemSelected: TreeViewDataItem,
  inDesignLinkedDocument: LinkedDocumentInfo | undefined
) {
  const showSendToInDesign = itemSelected.key == rootId && itemSelected.data.settings?.isInDesignEnabled;
  if (showSendToInDesign) {
    items.push({
      id: "sendtoindesign_menuitem",
      key: "sendtoindesign",
      text: formatMessage("cof.base.sendtoindesign"),
      onClick: () => {
        void sendDocumentToInDesign(itemSelected.data);
      },
    });
    items.push({
      key: "updated_indesign",
      text:
        formatMessage("cof.indesign.sent") +
        " " +
        formateDateToPreferredLanguage(inDesignLinkedDocument ? new Date(inDesignLinkedDocument.date) : undefined),
      hidden: !inDesignLinkedDocument?.date,
      disabled: "true",
      className: "sentDateUpdate",
    });
  }
}

export function isDocumentPartSubmitted(
  documentPartId: string,
  documentPartWithStatus: DocumentPartWithStatus[]
): boolean {
  if (!documentPartId || !documentPartWithStatus?.length) {
    return false;
  }
  return getWorkflowStatus(documentPartId, documentPartWithStatus)?.workflowStatusCode === SUBMITTED;
}

function haveSubmittedOrApprovedStatus(
  selectedItem: FlatPathTreeItem,
  documentPartWithStatus: DocumentPartWithStatus[]
) {
  let workflowStatus = getWorkflowStatus(selectedItem.key, documentPartWithStatus);
  return (
    workflowStatus?.workflowStatusCode === SUBMITTED ||
    workflowStatus?.workflowStatusCode === APPROVED1 ||
    workflowStatus?.workflowStatusCode === APPROVED2 ||
    workflowStatus?.workflowStatusCode === APPROVED3 ||
    workflowStatus?.workflowStatusCode === APPROVED4 ||
    workflowStatus?.workflowStatusCode === APPROVED5
  );
}
